import React from "react";
import { Link } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Link as RouterLink } from "react-router-dom";
import { useGlobalSearchContext } from "../SearchProvider";
import AppIcon from "ui/components/icons/AppIcon";
import { iconCheck, iconClose } from "ui/components/icons";
import { CommentContent } from "data/@types/GlobalSearchData";
import { useCommentColumnVisibilityStore } from "data/store/global-search/CommentColumnVisibility";
import {
  CommentTableRow,
  useGlobalSearchCommentsTable,
} from "data/hooks/useGlobalSearchCommentsTable";
import { formatDateTimeInUserTimeZone } from "utils/date";
import { valueOrPlaceholder } from "utils/form";
import {
  getSharedEntityColumns,
  GlobalSearchAdvancedTable,
} from "ui/components/global-search/GlobalSearchAdvancedTable";

type GlobalSearchCommentsTable = {
  onBack: () => void;
  commentsRows: CommentContent[];
  isLoading?: boolean;
};

const GlobalSearchCommentsTable = ({
  onBack,
  commentsRows,
  isLoading,
}: GlobalSearchCommentsTable) => {
  const columnVisibilityModel = useCommentColumnVisibilityStore(
    (state) => state.comment,
  );
  const onColumnVisibilityChange = useCommentColumnVisibilityStore(
    (state) => state.updateColumnVisibility,
  );

  const { handleClose } = useGlobalSearchContext();

  const { commentFilters, applyFilters, rows, isBackendLoading, fetchError } =
    useGlobalSearchCommentsTable(commentsRows);

  const commentColumns: GridColDef[] = [
    { field: "id", headerName: "Comment ID" },
    {
      headerName: "Entity Legacy ID",
      field: "entityLegacyId",
    },
    {
      headerName: "Entity RCU ID",
      field: "entityId",
    },
    {
      headerName: "Author ID",
      field: "scheduledById",
    },
    {
      field: "commentLink",
      headerName: "Comment Link",
      valueGetter: (_, row) => row?.commentLink,
      renderCell: ({ row }: GridRenderCellParams) => {
        const route = row.isAnnualMeetingNote
          ? `/annual-meeting-note/${row.id}`
          : `/comment/${row.id}`;
        return (
          <Link
            to={route}
            component={RouterLink}
            onClick={() => handleClose()}
            sx={{
              textDecoration: "none",
              color: "info.main",
              fontWeight: "bold",
            }}
          >
            {row?.commentLink}
          </Link>
        );
      },
      minWidth: 330,
      flex: 1,
    },
    ...getSharedEntityColumns(handleClose),
    { field: "authorName", headerName: "Author" },
    {
      field: "isAnnualMeetingNote",
      headerName: "Annual Meeting Note",
      align: "center",
      renderCell: ({ row }: GridRenderCellParams) => {
        return row.isAnnualMeetingNote ? (
          <AppIcon
            icon={iconCheck}
            fontSize="small"
            sx={{ color: "info.main" }}
          />
        ) : (
          <AppIcon icon={iconClose} fontSize="small" />
        );
      },
    },
    {
      field: "esg",
      headerName: "ESG",
      minWidth: 180,
      flex: 1,
      align: "center",
      renderCell: ({ row }: GridRenderCellParams) => {
        return row.esg ? (
          <AppIcon
            icon={iconCheck}
            fontSize="small"
            sx={{ color: "info.main" }}
          />
        ) : (
          <AppIcon icon={iconClose} fontSize="small" />
        );
      },
    },
    {
      field: "dei",
      headerName: "DEI",
      minWidth: 180,
      flex: 1,
      align: "center",
      renderCell: ({ row }: GridRenderCellParams) => {
        return row.dei ? (
          <AppIcon
            icon={iconCheck}
            fontSize="small"
            sx={{ color: "info.main" }}
          />
        ) : (
          <AppIcon icon={iconClose} fontSize="small" />
        );
      },
    },
    {
      field: "netZero",
      headerName: "Net Zero",
      align: "center",
      renderCell: ({ row }: GridRenderCellParams) => {
        return row.netZero ? (
          <AppIcon
            icon={iconCheck}
            fontSize="small"
            sx={{ color: "info.main" }}
          />
        ) : (
          <AppIcon icon={iconClose} fontSize="small" />
        );
      },
    },
    {
      field: "quarterlyMonitoring",
      headerName: "Quarterly Monitoring",
      align: "center",
      renderCell: ({ row }: GridRenderCellParams) => {
        return row.quarterlyMonitoring ? (
          <AppIcon
            icon={iconCheck}
            fontSize="small"
            sx={{ color: "info.main" }}
          />
        ) : (
          <AppIcon icon={iconClose} fontSize="small" />
        );
      },
    },
    {
      field: "quarter",
      headerName: "Quarter",
      minWidth: 100,
      valueFormatter: (_, row) => valueOrPlaceholder(row.quarter),
    },
    {
      field: "annualMonitoring",
      headerName: "Annual Monitoring",
      align: "center",
      renderCell: ({ row }: GridRenderCellParams) => {
        return row.annualMonitoring ? (
          <AppIcon
            icon={iconCheck}
            fontSize="small"
            sx={{ color: "info.main" }}
          />
        ) : (
          <AppIcon icon={iconClose} fontSize="small" />
        );
      },
    },

    {
      field: "sec",
      headerName: "SEC/Form ADV",
      minWidth: 140,
      flex: 1,
      align: "center",
      renderCell: ({ row }: GridRenderCellParams) => {
        return row.sec ? (
          <AppIcon
            icon={iconCheck}
            fontSize="small"
            sx={{ color: "info.main" }}
          />
        ) : (
          <AppIcon icon={iconClose} fontSize="small" />
        );
      },
    },
    {
      field: "annualMeetingCompliance",
      headerName: "Annual Meeting with Compliance",
      align: "center",
      renderCell: ({ row }: GridRenderCellParams) => {
        return row.annualMeetingCompliance ? (
          <AppIcon
            icon={iconCheck}
            fontSize="small"
            sx={{ color: "info.main" }}
          />
        ) : (
          <AppIcon icon={iconClose} fontSize="small" />
        );
      },
    },
    {
      field: "govComplete",
      headerName: "Gov Complete",
      align: "center",
      renderCell: ({ row }: GridRenderCellParams) => {
        return row.govComplete ? (
          <AppIcon
            icon={iconCheck}
            fontSize="small"
            sx={{ color: "info.main" }}
          />
        ) : (
          <AppIcon icon={iconClose} fontSize="small" />
        );
      },
    },
    {
      field: "oddComment",
      headerName: "ODD Comment",
      align: "center",
      renderCell: ({ row }: GridRenderCellParams) => {
        return row.oddComment ? (
          <AppIcon
            icon={iconCheck}
            fontSize="small"
            sx={{ color: "info.main" }}
            data-test-id="check-icon"
          />
        ) : (
          <AppIcon
            icon={iconClose}
            fontSize="small"
            data-test-id="close-icon"
          />
        );
      },
    },
    { field: "aiSummary", headerName: "CAi Summary" },
    { field: "restrictedText", headerName: "Restricted" },
    {
      field: "fundName",
      headerName: "AAIM Fund",
    },
    {
      field: "performanceSection",
      headerName: "Performance",
    },
    {
      field: "fundraisingSection",
      headerName: "Fundraising",
    },
    {
      field: "legalSection",
      headerName: "Legal",
    },
    {
      field: "createdByPersonName",
      headerName: "Created By",
    },
    {
      field: "creTimest",
      headerName: "Created Date",
      minWidth: 140,
      flex: 1,
      valueFormatter: (_, row) => formatDateTimeInUserTimeZone(row.creTimest),
    },
    {
      field: "updatedByPersonName",
      headerName: "Last Modified By",
    },
    {
      field: "modTimest",
      headerName: "Last Modified Date",
      minWidth: 140,
      flex: 1,
      valueFormatter: (_, row) => formatDateTimeInUserTimeZone(row.modTimest),
    },

    { field: "createdById", headerName: "Created By ID" },
    { field: "updatedById", headerName: "Last Modified By ID" },
  ];

  return (
    <GlobalSearchAdvancedTable<CommentContent, CommentTableRow>
      onBack={onBack}
      rows={rows}
      columns={commentColumns}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityChange={onColumnVisibilityChange}
      filters={commentFilters}
      applyFilters={applyFilters}
      activityType="Comments"
      getRowId={(row) =>
        `${row.entityType} ${row?.id} ${row.entityId} ${row?.fundName}`
      }
      emptyTestId="comments-table-view-empty"
      isLoading={isLoading || isBackendLoading}
      fetchError={fetchError}
    />
  );
};

export { GlobalSearchCommentsTable };
